// Internal modules
import { isDev, isTest } from '../utils/env';
import type { ResolverConfig, ResolverKey } from './resolvers';
import { resolveFeatures } from './resolvers';

export const features = {
  allActive(...feats: ResolverKey[]): boolean {
    return Boolean(feats.length) && feats.every(f => this.config[f]);
  },
  allNotActive(...feats: ResolverKey[]): boolean {
    return !feats.length || feats.every(f => !this.config[f]);
  },

  config: resolveFeatures(),

  // Until we type resolvers we don't know what they coudl return
  get<T extends ResolverKey>(name: T): ResolverConfig[T] {
    return this.config[name];
  },

  hasFeature(featureKey: ResolverKey, value?: string | string[]): boolean {
    const feat = this.config[featureKey];
    // We know not all features have variants
    // TODO: Can we cleanup code and move those out to their own area?
    const featureValue = feat?.variant || feat;

    if (value) {
      const valuesArray = Array.isArray(value) ? value : [value];

      return valuesArray.includes(featureValue);
    }
    return Boolean(feat);
  },

  isActive(feat: ResolverKey): boolean {
    return this.config[feat] === true;
  },

  isNotActive(feat: ResolverKey): boolean {
    return !this.isActive(feat);
  },

  someActive(...feats: ResolverKey[]): boolean {
    return !this.allNotActive(...feats);
  },
  someNotActive(...feats: ResolverKey[]): boolean {
    return !feats.length || feats.some(feat => !this.config[feat]);
  },
};

export type FeaturesType = typeof features;

if (isTest) {
  // @ts-expect-error - only used for tests, do not need to type
  features.set = feats => {
    features.config = {
      ...features.config,
      ...feats,
    };
  };
}

// Make it easy to look at features in dev
if (isDev) {
  // @ts-expect-error - only used for dev, do not need to type
  global.ppFeatures = features;
}
